/**
 * TikTok Pixel utility.
 * Loaded only after marketing consent is granted (via consentBridge).
 * Wraps window.ttq — the global injected by the TikTok pixel base script.
 */

const PIXEL_ID: string = import.meta.env.VITE_TIKTOK_PIXEL_ID ?? 'D8OIQHRC77U8H74TAD8G';

declare global {
  interface Window {
    ttq?: {
      load: (pixelId: string) => void;
      page: () => void;
      track: (
        event: string,
        params?: Record<string, unknown>,
        options?: { event_id?: string }
      ) => void;
      identify: (params: Record<string, unknown>) => void;
      instance: (pixelId: string) => any;
    };
  }
}

let initialized = false;

/** Dynamically inject the TikTok pixel base script and load the pixel. */
export function initTikTokPixel(): void {
  if (initialized || !PIXEL_ID) return;
  if (typeof window === 'undefined') return;

  const scriptId = 'tiktok-pixel-script';
  if (document.getElementById(scriptId)) return;

  // Inline stub so ttq calls queued before the script loads are not lost
  // TiktokAnalyticsObject tells events.js which global variable holds the queue
  (window as any).TiktokAnalyticsObject = 'ttq';
  (function () {
    const ttq: any = (window.ttq = (window.ttq as any) || []);
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function (t: any, e: string) {
      t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function (t: string) {
      const e: any = ttq._i[t] || [];
      for (let i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(e, ttq.methods[i]);
      }
      return e;
    };
    ttq.load = function (e: string, n?: any) {
      const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = i + '?sdkid=' + e + '&lib=ttq';
      s.id = scriptId;
      const f = document.getElementsByTagName('script')[0];
      f.parentNode!.insertBefore(s, f);
    };
  })();

  window.ttq!.load(PIXEL_ID);
  window.ttq!.page();
  initialized = true;

  if (!import.meta.env.PROD) {
    console.log('[TikTok Pixel] Initialized with pixel', PIXEL_ID);
  }
}

/** Track an SPA page view. No-op if pixel not initialized. */
export function ttqPage(): void {
  if (!initialized) return;
  window.ttq?.page();
  if (!import.meta.env.PROD) {
    console.log('[TikTok Pixel] page()', window.location.pathname);
  }
}

/** Fire a standard TikTok pixel event. No-op if pixel not initialized. */
function ttqTrack(
  event: string,
  params?: Record<string, unknown>,
  options?: { event_id?: string }
): void {
  if (!initialized) return;
  window.ttq?.track(event, params, options);
  if (!import.meta.env.PROD) {
    console.log(`[TikTok Pixel] track(${event})`, params, options);
  }
}

/**
 * Set user identifiers for advanced matching.
 * TikTok's events.js automatically SHA-256-hashes these values before sending.
 * Safe to call before the pixel finishes loading (queued by the stub).
 */
export function ttqIdentify(params: { email?: string; phone?: string; external_id?: string }): void {
  if (typeof window === 'undefined' || !window.ttq) return;
  const payload: Record<string, unknown> = {};
  if (params.email) payload.email = params.email.trim().toLowerCase();
  if (params.phone) payload.phone_number = params.phone.replace(/[^\d+]/g, '');
  if (params.external_id) payload.external_id = params.external_id;
  if (Object.keys(payload).length === 0) return;
  window.ttq.identify(payload);
  if (!import.meta.env.PROD) {
    console.log('[TikTok Pixel] identify()', Object.keys(payload));
  }
}

/** Convenience tracking object — mirrors the analytics.ts pattern. */
export const tiktok = {
  /** Activity detail page viewed → ViewContent */
  activityViewed: (activityId: string, activityName: string, price?: number) => {
    ttqTrack('ViewContent', {
      content_type: 'product',
      content_id: activityId,
      content_name: activityName,
      ...(price !== undefined && { value: price, currency: 'EUR' }),
    });
  },

  /** Slot selected / checkout started → InitiateCheckout */
  checkoutStarted: (activityId: string, activityName: string, value: number) => {
    ttqTrack('InitiateCheckout', {
      content_type: 'product',
      content_id: activityId,
      content_name: activityName,
      value,
      currency: 'EUR',
    });
  },

  /**
   * Booking confirmed after payment → CompletePayment
   * `bookingId` is also used as the event_id so this event can be deduplicated
   * against the server-side CAPI call from the stripe-webhook edge function.
   */
  bookingCompleted: (bookingId: string, value: number, activityId: string, activityName: string) => {
    ttqTrack(
      'CompletePayment',
      {
        content_type: 'product',
        content_id: activityId,
        content_name: activityName,
        value,
        currency: 'EUR',
      },
      { event_id: bookingId }
    );
  },

  /** New account created → CompleteRegistration */
  userRegistered: () => {
    ttqTrack('CompleteRegistration');
  },

  /** Search performed → Search */
  searched: (query: string) => {
    ttqTrack('Search', { query });
  },
};
